import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import {
  FileText,
  ArrowUpRight,
  ArrowRight,
} from "lucide-react";

import API_BASE_URL from "../../api/api";
import Header from "../../components/Header";
import Footer from "../../components/Footer";

const StudentCornerPage = () => {
  const { slug } = useParams();

  const [studentCornerPageData, setStudentCornerPageData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // ============================================================
  // FETCH STUDENT CORNER DOCUMENTS
  // ============================================================

  useEffect(() => {
    const fetchStudentCornerPageData = async () => {
      try {
        setLoading(true);
        setError("");
        setStudentCornerPageData([]);

        if (!slug) {
          setError("Student Corner section not found.");
          return;
        }

        const apiUrl =
          `${API_BASE_URL}/studentCornerDocuments?section=${encodeURIComponent(slug)}`;

        console.log("Student Corner API URL:", apiUrl);

        const response = await fetch(apiUrl);

        if (!response.ok) {
          throw new Error(`API Error: ${response.status}`);
        }

        const result = await response.json();

        console.log("Student Corner API Response:", result);

        if (result.status === true && Array.isArray(result.data)) {
          setStudentCornerPageData(result.data);

          if (result.data.length === 0) {
            setError("No documents available for this section.");
          }
        } else {
          setStudentCornerPageData([]);
          setError(result.message || "No documents found.");
        }
      } catch (err) {
        console.error("Student Corner API Error:", err);

        setStudentCornerPageData([]);

        setError("Unable to load Student Corner documents.");
      } finally {
        setLoading(false);
      }
    };

    fetchStudentCornerPageData();
  }, [slug]);

  // ============================================================
  // PAGE TITLE
  // ============================================================

  const pageTitle = formatSlugTitle(slug);

  return (
    <div className="min-h-screen bg-[var(--paper)]">

      <Header />

      {/* ============================================================
          HERO
      ============================================================ */}

      <section className="relative h-[300px] overflow-hidden text-lg font-gill">

        <img
          src="/images/campusKumaun.avif"
          alt="Kumaun University"
          className="absolute inset-0 w-full h-full object-cover"
        />

        {/* Overlay */}
        <div className="absolute inset-0 bg-gradient-to-r from-[#d1fae5]/95 via-[#a7f3d0]/80 to-[#ecfdf5]/40" />

        <div className="relative z-10 max-w-7xl mx-auto h-full px-4 flex items-center">

          <div className="max-w-4xl">

            {/* Breadcrumb */}
            <div className="flex items-center gap-2 text-sm mb-3 flex-wrap">

              <span className="text-[#123B5D] text-lg">
                Home
              </span>

              <span className="text-[#123B5D] text-lg">
                /
              </span>

              <span className="text-[#123B5D] text-lg">
                Student Corner
              </span>

              <span className="text-[#123B5D] text-lg">
                /
              </span>

              <span className="text-[#123B5D] text-lg">
                {pageTitle}
              </span>

            </div>

            <h1
              className="
                text-3xl
                md:text-5xl
                font-bold
                leading-tight
                text-[#064E3B]
                uppercase
              "
            >
              {pageTitle}
            </h1>

          </div>

        </div>
      </section>


      {/* ============================================================
          CONTENT
      ============================================================ */}

      <section
        className="
          relative
          overflow-hidden
          bg-[#f8fafc]
          py-16
          md:py-20
          text-lg
          font-gill
        "
      >

        {/* Background Shapes */}

        <div
          className="
            absolute
            -top-32
            -left-32
            w-80
            h-80
            rounded-full
            bg-[#B5121B]/5
          "
        />

        <div
          className="
            absolute
            -bottom-40
            -right-32
            w-96
            h-96
            rounded-full
            bg-[#08245c]/5
          "
        />

        <div className="relative max-w-7xl mx-auto px-6">

          {/* ========================================================
              HEADER
          ======================================================== */}

          <div className="text-center">

            <div
              className="
                inline-flex
                items-center
                gap-2
                px-4
                py-2
                rounded-full
                bg-[#fff1f1]
                border
                border-[#B5121B]/10
              "
            >

              <span
                className="
                  w-2
                  h-2
                  rounded-full
                  bg-[#B5121B]
                  animate-pulse
                "
              />

              <span
                className="
                  text-[11px]
                  md:text-xs
                  font-bold
                  uppercase
                  tracking-[0.18em]
                  text-[#B5121B]
                "
              >
                {pageTitle}
              </span>

            </div>


            {/* Divider */}

            <div
              className="
                mt-6
                flex
                items-center
                justify-center
                gap-2
              "
            >

              <span
                className="
                  w-16
                  h-[3px]
                  rounded-full
                  bg-[#B5121B]
                "
              />

              <span
                className="
                  w-3
                  h-3
                  rotate-45
                  border-2
                  border-[#08245c]
                "
              />

              <span
                className="
                  w-16
                  h-[3px]
                  rounded-full
                  bg-[#08245c]
                "
              />

            </div>

          </div>


          {/* ========================================================
              DOCUMENT CARDS
          ======================================================== */}

          <DocumentCards
            documents={studentCornerPageData}
            loading={loading}
            error={error}
          />

        </div>

      </section>


      <Footer />

    </div>
  );
};


// ================================================================
// FORMAT SLUG TITLE
// ================================================================

const formatSlugTitle = (slug) => {
  if (!slug) {
    return "Student Corner";
  }

  return slug
    .replace(/[-_]/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());
};


// ================================================================
// FORMAT EVENT TYPE
// ================================================================

const formatEventType = (value) => {
  if (!value) {
    return "";
  }

  return value
    .replace(/[-_]/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());
};


// ================================================================
// DOCUMENT CARDS
// ================================================================

const DocumentCards = ({
  documents,
  loading,
  error,
}) => {

  // ==============================================================
  // LOADING
  // ==============================================================

  if (loading) {
    return (
      <div className="mt-12 text-center py-10 text-gray-500">

        <div className="inline-flex items-center gap-3">

          <div
            className="
              w-5
              h-5
              border-2
              border-gray-300
              border-t-[#B5121B]
              rounded-full
              animate-spin
            "
          />

          <span>
            Loading documents...
          </span>

        </div>

      </div>
    );
  }


  // ==============================================================
  // ERROR
  // ==============================================================

  if (error && (!documents || documents.length === 0)) {
    return (
      <div className="mt-12 text-center py-10">

        <FileText
          size={42}
          className="mx-auto mb-4 text-gray-400"
        />

        <p className="text-gray-500">
          {error}
        </p>

      </div>
    );
  }


  // ==============================================================
  // NO DATA
  // ==============================================================

  if (!documents || documents.length === 0) {
    return (
      <div className="mt-12 text-center py-10">

        <FileText
          size={42}
          className="mx-auto mb-4 text-gray-400"
        />

        <p className="text-gray-500">
          No documents available.
        </p>

      </div>
    );
  }


  // ==============================================================
  // DOCUMENT LIST
  // ==============================================================

  return (
    <div className="mt-10 grid md:grid-cols-2 gap-4">

      {documents.map((item) => {

        const documentUrl =
          item.pdf_path || item.external_link;

        return (
          <a
            key={item.id}
            href={documentUrl || "#"}
            target={documentUrl ? "_blank" : undefined}
            rel={documentUrl ? "noopener noreferrer" : undefined}
            onClick={(event) => {
              if (!documentUrl) {
                event.preventDefault();
              }
            }}
            className="
              group
              flex
              items-start
              gap-4
              rounded-2xl
              bg-white
              p-5
              border
              border-[var(--cream-deep)]
              card-lift
              hover:shadow-lg
              transition-all
              font-gill
            "
          >

            {/* ====================================================
                ICON + PUBLISH DATE
            ==================================================== */}

            <div
              className="
                shrink-0
                w-14
                min-h-14
                rounded-xl
                bg-[var(--terracotta)]/10
                flex
                flex-col
                items-center
                justify-center
                px-1
                py-2
              "
            >

              <FileText
                size={25}
                className="text-[var(--terracotta)]"
              />

              {item.publish_date && (
                <span
                  className="
                    text-[15px]
                    text-blue-500
                    font-bold
                    mt-2
                    whitespace-nowrap
                  "
                >
                  {item.publish_date}
                </span>
              )}

            </div>


            {/* ====================================================
                DIVIDER
            ==================================================== */}

            <div className="h-14 w-px bg-[var(--cream-deep)]" />


            {/* ====================================================
                CONTENT
            ==================================================== */}

            <div className="flex-1 min-w-0">

              {/* Heading */}

              <div
                className="
                  text-[14.5px]
                  leading-snug
                  text-[var(--ink)]
                  group-hover:text-[var(--terracotta)]
                  transition-colors
                "
              >
                {item.heading || "Untitled Document"}
              </div>


              {/* Event Type */}

              {/* {item.event_type && (
                <div
                  className="
                    mt-2
                    flex
                    items-center
                    gap-2
                    text-[11.5px]
                    font-semibold
                    text-[var(--terracotta)]
                    opacity-80
                  "
                >

                  <span>
                    {formatEventType(item.event_type)}
                  </span>

                  <ArrowUpRight size={12} />

                </div>
              )} */}


              {/* View Details */}

              {documentUrl && (
                <div
                  className="
                    mt-3
                    inline-flex
                    items-center
                    gap-1.5
                    text-xs
                    font-bold
                    text-[#08245c]
                    group-hover:text-[#B5121B]
                  "
                >
                  View Details

                  <ArrowUpRight size={14} />

                </div>
              )}

            </div>


            {/* ====================================================
                RIGHT ARROW
            ==================================================== */}

            <div
              className="
                hidden
                sm:flex
                shrink-0
                w-10
                h-10
                rounded-full
                bg-[#f8fafc]
                items-center
                justify-center
                text-[#08245c]
                group-hover:bg-[#fff1f1]
                group-hover:text-[#B5121B]
                transition-all
              "
            >

              <ArrowRight size={17} />

            </div>

          </a>
        );
      })}

    </div>
  );
};


export default StudentCornerPage;